import { isUsableLatLng } from './liveLocation';

export const DEFAULT_FLEET_CENTER = [28.5, -81.4];
export const DEFAULT_FLEET_ZOOM = 11;
export const SERVICE_DRIVER_RADIUS_MILES = 90;

const EARTH_RADIUS_MILES = 3958.8;
const ACTIVE_TRIP_STATUSES = new Set(['Assigned', 'EnRoute', 'PickedUp']);

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const sameId = (left, right) => (
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right)
);

export const distanceMilesBetween = (origin, target) => {
  if (!Array.isArray(origin) || !Array.isArray(target)) return null;
  const [lat1, lon1] = origin.map(Number);
  const [lat2, lon2] = target.map(Number);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;

  const deltaLat = toRadians(lat2 - lat1);
  const deltaLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
};

export const normalizeFleetCoords = (location) => {
  if (!location) return null;

  if (isUsableLatLng(location.lat, location.lng)) {
    return [Number(location.lat), Number(location.lng)];
  }
  if (isUsableLatLng(location.latitude, location.longitude)) {
    return [Number(location.latitude), Number(location.longitude)];
  }
  if (Array.isArray(location.coordinates) && location.coordinates.length >= 2) {
    const latitude = Number(location.coordinates[1]);
    const longitude = Number(location.coordinates[0]);
    if (isUsableLatLng(latitude, longitude)) return [latitude, longitude];
  }
  return null;
};

export const locationTimestamp = (location, fallback) => (
  location?.updatedAt ||
  location?.serverReceivedAt ||
  location?.receivedAt ||
  location?.recordedAt ||
  fallback ||
  null
);

export const getFleetLocationFreshness = (updatedAt, now = Date.now(), motionState = 'unknown') => {
  const at = updatedAt ? new Date(updatedAt).getTime() : NaN;
  if (!Number.isFinite(at)) return { state: 'unknown', label: 'No recent ping' };

  const ageSeconds = Math.max(0, Math.round((now - at) / 1000));
  const freshSeconds = motionState === 'moving' ? 20 : 75;
  const staleSeconds = motionState === 'moving' ? 60 : 180;
  if (ageSeconds <= freshSeconds) return { state: 'fresh', label: `${ageSeconds}s ago` };
  if (ageSeconds <= staleSeconds) return { state: 'stale', label: `${ageSeconds}s ago - stale` };
  return { state: 'warning', label: `${ageSeconds}s ago - very stale` };
};

export const normalizeActiveFleetRecord = (record) => {
  if (!record) return null;

  const driver = record.driver || record.driverInfo || {};
  const vehicle = record.vehicle || record.vehicleInfo || record.cab || {};
  const rawLocation =
    record.currentLocation ||
    record.location ||
    driver.currentLocation ||
    vehicle.currentLocation ||
    null;
  const driverId = record.driverId || driver.driverId || driver._id || driver.id || '';
  const cabNumber = record.cabNumber || vehicle.cabNumber || '';

  return {
    _id: record._id || `${driverId}-${cabNumber}`,
    driverId,
    firstName: record.firstName || driver.firstName || '',
    lastName: record.lastName || driver.lastName || '',
    cabNumber,
    availability: record.availability || record.currentAvailability || 'Offline',
    status: record.status || record.currentStatus || 'Inactive',
    updatedAt: locationTimestamp(rawLocation, record.updatedAt),
    position: normalizeFleetCoords(rawLocation),
    speed: Number(rawLocation?.speed ?? rawLocation?.speedMps ?? 0),
    heading: Number(rawLocation?.heading ?? 0),
    accuracy: Number(rawLocation?.accuracy ?? 0),
    sequence: Number(rawLocation?.sequence ?? 0),
    motionState: rawLocation?.motionState || 'unknown',
    trackingHealth: rawLocation?.trackingHealth || 'unknown',
    breadcrumbs: normalizeFleetCoords(rawLocation) ? [normalizeFleetCoords(rawLocation)] : [],
    onTrip: Boolean(record.currentTrip && ACTIVE_TRIP_STATUSES.has(record.currentTrip.status)),
  };
};

export const getFleetCounts = (drivers = []) => drivers.reduce(
  (counts, driver) => {
    if (!driver?.position) {
      counts.noLocation += 1;
      return counts;
    }

    counts.located += 1;
    if (driver.onTrip) counts.onTrip += 1;
    else if (driver.availability === 'Online') counts.available += 1;
    else counts.offline += 1;
    return counts;
  },
  { available: 0, onTrip: 0, offline: 0, noLocation: 0, located: 0 },
);

export const getVisibleFleetDrivers = ({
  drivers = [],
  filter = 'all',
  serviceCenter = null,
  radiusMiles = SERVICE_DRIVER_RADIUS_MILES,
} = {}) => drivers.filter((driver) => {
  if (!driver?.position) return false;
  if (filter === 'online' && (driver.availability !== 'Online' || driver.onTrip)) return false;
  if (filter === 'ontrip' && !driver.onTrip) return false;
  if (filter === 'offline' && (driver.availability !== 'Offline' || driver.onTrip)) return false;
  if (!serviceCenter) return true;

  const miles = distanceMilesBetween(serviceCenter, driver.position);
  return miles === null || miles <= radiusMiles;
});

const findDriverIndex = (drivers, driverId, recordId) => drivers.findIndex((driver) => (
  sameId(driver.driverId, driverId) || sameId(driver._id, recordId)
));

export const applyFleetLocationEvent = (drivers, payload = {}, fallbackAt = new Date().toISOString()) => {
  const { booking, driverId, active } = payload;
  const normalizedActive = active ? normalizeActiveFleetRecord(active) : null;
  const resolvedDriverId = booking?.driverId || driverId || normalizedActive?.driverId;
  const rawLocation = payload.driverLocation || active?.currentLocation || null;
  const position = normalizeFleetCoords(rawLocation);
  if (!resolvedDriverId || !position) return drivers;

  const updatedAt = locationTimestamp(rawLocation, fallbackAt);
  const onTrip = booking ? ACTIVE_TRIP_STATUSES.has(booking.status) : normalizedActive?.onTrip || false;
  const index = findDriverIndex(drivers, resolvedDriverId, normalizedActive?._id);

  if (index === -1) {
    return [
      ...drivers,
      {
        _id: normalizedActive?._id || resolvedDriverId,
        driverId: resolvedDriverId,
        firstName: normalizedActive?.firstName || '',
        lastName: normalizedActive?.lastName || '',
        cabNumber: booking?.cabNumber || normalizedActive?.cabNumber || '',
        availability: normalizedActive?.availability || 'Online',
        status: normalizedActive?.status || 'Active',
        updatedAt,
        position,
        speed: Number(rawLocation?.speed ?? rawLocation?.speedMps ?? 0),
        heading: Number(rawLocation?.heading ?? 0),
        accuracy: Number(rawLocation?.accuracy ?? 0),
        sequence: Number(rawLocation?.sequence ?? 0),
        motionState: rawLocation?.motionState || 'unknown',
        trackingHealth: rawLocation?.trackingHealth || 'unknown',
        breadcrumbs: [position],
        onTrip,
      },
    ];
  }

  return drivers.map((driver, driverIndex) => (
    driverIndex === index
      ? {
          ...driver,
          ...(normalizedActive || {}),
          position,
          updatedAt,
          speed: Number(rawLocation?.speed ?? rawLocation?.speedMps ?? driver.speed ?? 0),
          heading: Number(rawLocation?.heading ?? driver.heading ?? 0),
          accuracy: Number(rawLocation?.accuracy ?? driver.accuracy ?? 0),
          sequence: Number(rawLocation?.sequence ?? driver.sequence ?? 0),
          motionState: rawLocation?.motionState || driver.motionState || 'unknown',
          trackingHealth: rawLocation?.trackingHealth || driver.trackingHealth || 'unknown',
          breadcrumbs: [...(driver.breadcrumbs || []), position].slice(-30),
          cabNumber: driver.cabNumber || booking?.cabNumber || normalizedActive?.cabNumber || '',
          availability: normalizedActive?.availability || driver.availability || 'Online',
          onTrip: booking ? onTrip : driver.onTrip,
        }
      : driver
  ));
};

export const applyFleetPresenceEvent = (drivers, payload = {}) => {
  const normalized = normalizeActiveFleetRecord(payload.active);
  if (!normalized?.driverId) return drivers;

  const index = findDriverIndex(drivers, normalized.driverId, normalized._id);
  if (index === -1) return [...drivers, normalized];

  return drivers.map((driver, driverIndex) => (
    driverIndex === index
      ? {
          ...driver,
          ...normalized,
          position: normalized.position ?? driver.position,
          updatedAt: normalized.updatedAt || driver.updatedAt,
          onTrip: normalized.onTrip || driver.onTrip,
        }
      : driver
  ));
};

export const applyFleetAssignmentEvent = (drivers, payload = {}) => {
  const booking = payload.booking;
  if (!booking?.driverId) return drivers;
  const onTrip = ACTIVE_TRIP_STATUSES.has(booking.status);

  return drivers.map((driver) => (
    sameId(driver.driverId, booking.driverId) ? { ...driver, onTrip } : driver
  ));
};

export const applyFleetHeartbeatEvent = (drivers, payload = {}, fallbackAt = new Date().toISOString()) => {
  const booking = payload.booking;
  if (!booking) return drivers;
  const assignmentUpdated = applyFleetAssignmentEvent(drivers, { booking });
  if (!booking.driverLocation) return assignmentUpdated;

  return applyFleetLocationEvent(
    assignmentUpdated,
    { booking, driverLocation: booking.driverLocation },
    fallbackAt,
  );
};
